import { lazy, Suspense, type ComponentType } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from "react-router-dom";
import { ThemeProvider } from "./lib/theme";
import { DialogProvider } from "./lib/dialog";
import { UpgradeModalProvider } from "./components/UpgradeModalProvider";
import { getProduct } from "@/lib/product";
import { safeNextPath } from "@/lib/nextPath";

function RedirectDropletDetail() {
  const { id } = useParams();
  return <Navigate to={`/droplets/${id}`} replace />;
}

function RedirectToNext() {
  const { search } = useLocation();
  return <Navigate to={safeNextPath(new URLSearchParams(search).get("next"))} replace />;
}

function RedirectDnsRecords() {
  const { domainId } = useParams();
  return <Navigate to={`/networking/${domainId}/records`} replace />;
}

const staleChunkReloadKey = "gerdoo:stale-chunk-reload";

function lazyRoute<P extends object>(
  importer: () => Promise<{ default: ComponentType<P> }>,
) {
  return lazy(async () => {
    try {
      const module = await importer();
      if (!module?.default) throw new Error("Lazy route module is unavailable");
      return module;
    } catch (error) {
      const previousReload = sessionStorage.getItem(staleChunkReloadKey);
      if (!previousReload) {
        sessionStorage.setItem(staleChunkReloadKey, String(Date.now()));
        window.location.reload();
        return new Promise<never>(() => undefined);
      }
      throw error;
    }
  });
}

const Signin = lazyRoute(() => import("./pages/Signin"));
const Signup = lazyRoute(() => import("./pages/Signup"));
const VerifyEmail = lazyRoute(() => import("./pages/VerifyEmail"));
const ForgotPassword = lazyRoute(() => import("./pages/ForgotPassword"));
const AppLayout = lazyRoute(() => import("./components/layout/AppLayout"));
const Dashboard = lazyRoute(() => import("./pages/compute/Dashboard"));
const ServerDetails = lazyRoute(() => import("./pages/compute/ServerDetails"));
const Servers = lazyRoute(() => import("./pages/compute/Servers"));
const Networking = lazyRoute(() => import("./pages/networking/Networking"));
const AccessControls = lazyRoute(() => import("./pages/networking/AccessControls"));
const ManageRecords = lazyRoute(() => import("./pages/networking/ManageRecords"));
const TicketsList = lazyRoute(() => import("./pages/support/TicketsList"));
const TicketThread = lazyRoute(() => import("./pages/support/TicketThread"));
const NewTicket = lazyRoute(() => import("./pages/support/NewTicket"));
const Billing = lazyRoute(() => import("./pages/billing/Billing"));
const Invoice = lazyRoute(() => import("./pages/billing/Invoice"));
const Affiliate = lazyRoute(() => import("./pages/affiliate/Affiliate"));
const Marketplace = lazyRoute(() => import("./pages/marketplace/Marketplace"));
const AiAgent = lazyRoute(() => import("./pages/ai/AiAgent"));
const CliLogin = lazyRoute(() => import("./pages/CliLogin"));
const DeployTemplate = lazyRoute(() => import("./pages/marketplace/DeployTemplate"));
const Settings = lazyRoute(() => import("./pages/settings/Settings"));
const GitConnections = lazyRoute(() => import("./pages/settings/GitConnections"));
const ApiTokens = lazyRoute(() => import("./pages/settings/ApiTokens"));
const ServiceDetail = lazyRoute(() => import("./pages/services/ServiceDetail"));
const Services = lazyRoute(() => import("./pages/services/Services"));
const Notifications = lazyRoute(() => import("./pages/Notifications"));
const ProjectDetails = lazyRoute(() => import("./pages/projects/ProjectDetails"));
const CanvasHome = lazyRoute(() => import("./pages/projects/CanvasHome"));
const AddonDetail = lazyRoute(() => import("./pages/projects/AddonDetail"));
const UiKit = lazyRoute(() => import("./pages/dev/UiKit"));
const NotFound = lazyRoute(() => import("./pages/NotFound"));

function NewServiceRedirect() {
  const params = new URLSearchParams(useLocation().search);
  const project = params.get("project");
  const source = params.get("source") || "1";
  return <Navigate to={project ? `/projects/${project}?newService=${source}` : `/canvas?newService=${source}`} replace />;
}

function DashboardOrCanvas() {
  return getProduct() === "vps" ? <Dashboard /> : <Navigate to="/canvas" replace />;
}

function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-sm text-muted-foreground">
      در حال بارگذاری...
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        {/* Inside the router: dialog bodies (e.g. CostImpact) render <Link>s. */}
        <DialogProvider />
        <UpgradeModalProvider />
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* PaaS users land on their app's canvas; the VPS side keeps «امروز». */}
            <Route path="/" element={<Navigate to={getProduct() === "vps" ? "/dashboard" : "/canvas"} replace />} />
            <Route path="/signin" element={<Signin />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            <Route element={<AppLayout />}>
              {/* Onboarding is a modal over any page now; old links land on next. */}
              <Route path="/complete-profile" element={<RedirectToNext />} />
              {/* «امروز» is the VPS side's home only; on the PaaS side it is the canvas. */}
              <Route path="/dashboard" element={<DashboardOrCanvas />} />

              {/* Legacy dashboard aliases → canonical droplets / settings */}
              <Route
                path="/dashboard/create"
                element={<Navigate to="/droplets" replace />}
              />
              <Route
                path="/dashboard/servers"
                element={<Navigate to="/droplets" replace />}
              />
              <Route
                path="/dashboard/servers/:id"
                element={<RedirectDropletDetail />}
              />
              <Route
                path="/dashboard/ssh-keys"
                element={<Navigate to="/settings?tab=ssh-keys" replace />}
              />

              <Route path="/droplets" element={<Servers />} />
              <Route path="/droplets/new" element={<Navigate to="/droplets" replace />} />
              <Route path="/droplets/:id" element={<ServerDetails />} />

              {/* Legacy DNS aliases → networking */}
              <Route
                path="/dns"
                element={<Navigate to="/networking" replace />}
              />
              <Route
                path="/dns/:domainId/records"
                element={<RedirectDnsRecords />}
              />

              <Route path="/networking" element={<Networking />} />
              <Route
                path="/networking/:domainId/records"
                element={<ManageRecords />}
              />
              <Route path="/networking/access" element={<AccessControls />} />

              <Route path="/marketplace" element={<Marketplace />} />
              {/* Approval screen for the browser `gerdoo login` device flow. */}
              <Route path="/cli-login" element={<CliLogin />} />
              <Route path="/marketplace/:slug" element={<DeployTemplate />} />

              <Route path="/ai" element={<AiAgent />} />
              <Route path="/canvas" element={<CanvasHome />} />
              {/* No app list page: the canvas is the PaaS home. */}
              <Route path="/projects" element={<Navigate to="/canvas" replace />} />
              {/* Creating an app is a dialog over the canvas now. */}
              <Route path="/projects/new" element={<Navigate to="/canvas?newApp=1" replace />} />
              <Route path="/projects/:id" element={<ProjectDetails />} />
              <Route path="/projects/:id/addons/:addonId" element={<AddonDetail />} />

              <Route path="/services" element={<Services />} />
              {/* Adding a service is a dialog over the app's canvas now. */}
              <Route path="/services/new" element={<NewServiceRedirect />} />
              <Route path="/services/:id" element={<ServiceDetail />} />

              <Route path="/git" element={<GitConnections />} />
              <Route path="/tokens" element={<ApiTokens />} />
              <Route path="/settings" element={<Settings />} />
              <Route
                path="/settings/ssh-keys"
                element={<Navigate to="/settings?tab=ssh-keys" replace />}
              />
              <Route
                path="/settings/git"
                element={<Navigate to="/git" replace />}
              />
              <Route
                path="/settings/financial-info"
                element={<Navigate to="/settings?tab=financial" replace />}
              />

              <Route path="/notifications" element={<Notifications />} />

              <Route path="/support" element={<TicketsList />} />
              <Route path="/support/new" element={<NewTicket />} />
              <Route path="/support/:id" element={<TicketThread />} />

              <Route
                path="/cdn"
                element={<Navigate to="/dashboard" replace />}
              />
              <Route
                path="/storage"
                element={<Navigate to="/dashboard" replace />}
              />
              <Route
                path="/serverless"
                element={<Navigate to="/dashboard" replace />}
              />

              <Route path="/billing" element={<Billing />} />
              <Route path="/billing/invoices/:id" element={<Invoice />} />
              <Route path="/affiliate" element={<Affiliate />} />
              <Route
                path="/plans"
                element={<Navigate to="/billing?tab=plan" replace />}
              />
              {import.meta.env.DEV && (
                <Route path="/dev/ui" element={<UiKit />} />
              )}

              {/* Catch-all: unknown paths keep the app chrome (sidebar, topbar)
                  so a mistyped URL stays inside the dashboard. */}
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
